import { useMemo } from 'react';

import { useAllGroupZoomSchedulesQuery } from 'core/graphql';
import { weekDayToInt, formatRunTime } from 'core/utils/zoomSchedule';

/** One auto-processing slot of a group's Zoom schedule. */
export interface IScheduleSlot {
  id: string;
  weekDay: number;
  runTime: string;
}

/** A slot together with the group it belongs to — for the overview grid. */
export interface IScheduleSlotWithGroup extends IScheduleSlot {
  groupId: string;
  groupName: string;
}

interface IGroupSchedulesHook {
  /** Slots grouped by `group.id`. */
  schedulesByGroupId: Record<string, IScheduleSlot[]>;
  /** Flat list of every slot with its group — for the grid + conflict checks. */
  allSlots: IScheduleSlotWithGroup[];
  loading: boolean;
  error?: string;
  refetch: () => void;
}

/**
 * Feeds the `/zoom/schedule` tab: every group's Zoom auto-processing slots,
 * both grouped by group and as a flat list. See docs/zoom_schedule_tz.md.
 */
export const useGroupSchedules = (): IGroupSchedulesHook => {
  const { data, loading, error, refetch } = useAllGroupZoomSchedulesQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const allSlots = useMemo<IScheduleSlotWithGroup[]>(
    () =>
      (data?.allGroupZoomSchedules?.edges ?? [])
        .map((edge) => edge?.node)
        .filter((node): node is NonNullable<typeof node> => Boolean(node))
        .map((node) => ({
          id: node.id,
          weekDay: weekDayToInt(node.weekDay),
          runTime: formatRunTime(node.runTime),
          groupId: node.group.id,
          groupName: node.group.name,
        })),
    [data],
  );

  const schedulesByGroupId = useMemo(() => {
    const map: Record<string, IScheduleSlot[]> = {};
    allSlots.forEach((slot) => {
      (map[slot.groupId] = map[slot.groupId] ?? []).push({
        id: slot.id,
        weekDay: slot.weekDay,
        runTime: slot.runTime,
      });
    });
    return map;
  }, [allSlots]);

  return {
    schedulesByGroupId,
    allSlots,
    loading,
    error: error?.message,
    refetch: () => {
      refetch();
    },
  };
};
